import { MenuItem } from "../menu/provider/MenuItem";
import {
  MenuProvider,
  vsProviderName as menuProviderName,
} from "../menu/provider/menuProvider";
import {
  vsName as menuServiceName,
  MenuService,
} from "../menu/provider/MenuService";
import { vsName as userProfileName, UserProfile } from "./UserProfile";

export function menuRun(menuService: MenuService, userProfile: UserProfile) {
  menuService.setProfile(userProfile);
}
menuRun.$inject = [menuServiceName, userProfileName];

export function menuConfig(
  menuProvider: MenuProvider,
  applicationInfo: { isModuleLoaded(name: string): boolean }
) {
  const isModuleLoaded: (name: string) => boolean =
    applicationInfo.isModuleLoaded.bind(applicationInfo);
  const machineMenuItem: MenuItem | undefined = isModuleLoaded("machines")
    ? machineMenu(isModuleLoaded)
    : undefined;
  const progressiveMenuItem: MenuItem | undefined = isModuleLoaded(
    "progressives"
  )
    ? progressiveMenu()
    : undefined;
  const partsMenuItem: MenuItem | undefined = isModuleLoaded("parts")
    ? partsMenu()
    : undefined;
  const workOrdersMenuItem: MenuItem | undefined = isModuleLoaded("workOrders")
    ? workOrdersMenu()
    : undefined;
  const usersMenuItem: MenuItem = userMenu();
  const projectMenuItem: MenuItem = projectMenu();

  if (machineMenuItem !== undefined) {
    menuProvider.addMenuItem(machineMenuItem);
  }
  if (projectMenu !== undefined) {
    menuProvider.addMenuItem(projectMenuItem);
  }
  if (progressiveMenuItem !== undefined) {
    menuProvider.addMenuItem(progressiveMenuItem);
  }
  if (partsMenuItem !== undefined) {
    menuProvider.addMenuItem(partsMenuItem);
  }
  if (workOrdersMenuItem !== undefined) {
    menuProvider.addMenuItem(workOrdersMenuItem);
  }
  if (usersMenuItem !== undefined) {
    menuProvider.addMenuItem(usersMenuItem);
  }
}
menuConfig.$inject = [menuProviderName, "applicationInfo"];

function userMenu(): MenuItem {
  return new MenuItem({
    id: "users",
    display: "Users",
    state: "main.users.grid",
    activeOnState: "main.users.**",
    imgSrc: require("../menu/img/user_icon.png"),
    anyPermission: ["userModify", "userPasswordSet", "userLock"],
    children: [
      new MenuItem({
        id: "users.roles",
        state: "main.users.roles.grid",
        activeOnState: "main.users.roles.**",
        display: "Roles",
        requiredPermission: "userModify",
      }),
    ],
  });
}

function workOrdersMenu(): MenuItem {
  return new MenuItem({
    id: "workOrders",
    state: "main.workorders.grid",
    imgSrc: require("../menu/img/up_down_arrow.png"),
    display: "Work Orders",
    requiredPermission: "workOrderView",
  });
}

function partsMenu(): MenuItem {
  return new MenuItem({
    id: "parts",
    state: "main.parts.main",
    activeOnState: "main.parts.**",
    display: "Parts",
    imgSrc: require("../menu/img/parts_icon.png"),
    requiredPermission: "partView",
    children: [
      new MenuItem({
        id: "parts.checkout",
        state: "main.parts.checkout",
        display: "Checkout",
      }),
      new MenuItem({
        id: "parts.orders",
        state: "main.parts.orders",
        display: "Orders",
      }),
      new MenuItem({
        id: "parts.checkedOut",
        state: "main.parts.checkedOut",
        display: "Checkout List",
      }),
      new MenuItem({
        id: "parts.partProperties",
        state: "main.parts.partProperties",
        display: "Part Rooms",
      }),
    ],
  });
}

function progressiveMenu(): MenuItem {
  const progressiveMenuItem: MenuItem = new MenuItem({
    id: "progressives",
    state: "main.machines.progressives.progressiveDefinitions",
    display: "Progressives",
    imgSrc: require("../menu/img/progressives.png"),
    requiredPermission: "machines.progressives.read",
    activeOnState: "main.machines.progressives.progressiveDefinitions.**",
    children: [
      new MenuItem({
        id: "progressives.controllers",
        state: "main.machines.progressives.controllers",
        activeOnState: "main.machines.progressives.controllers.**",
        display: "Controllers",
      }),
      new MenuItem({
        id: "progressives.meterReadingSettings",
        state: "main.machines.progressives.meterReadingSettings",
        activeOnState: "main.machines.progressives.meterReadingSettings.**",
        display: "Meter Reading Settings",
      }),
      new MenuItem({
        id: "progressives.progressiveReadingValidationSettings",
        state:
          "main.machines.progressives.progressiveReadingValidationSettings",
        activeOnState:
          "main.machines.progressives.progressiveReadingValidationSettings.**",
        display: "Validation Settings",
      }),
      new MenuItem({
        id: "progressives.progressiveDailyReading",
        state:
          "main.machines.progressives.progressiveDailyReading.dailyReadings",
        display: "Daily Readings",
      }),
      new MenuItem({
        id: "progressives.escrowReadings",
        state: "main.machines.progressives.escrowReadings.readings",
        activeOnState: "main.machines.progressives.escrowReadings.**",
        display: "Escrow Readings",
      }),
      new MenuItem({
        id: "progressives.escrowReadingSettings",
        state: "main.machines.progressives.escrowReadingSettings",
        activeOnState: "main.machines.progressives.escrowReadingSettings.**",
        display: "Escrow Reading Settings",
      }),
    ],
  });

  return progressiveMenuItem;
}

function machineMenu(isModuleLoaded: (name: string) => boolean): MenuItem {
  const machineMenuItem: MenuItem = new MenuItem({
    id: "machines",
    state: "main.machines.grid",
    display: "Machines",
    requiredPermission: "machineView",
    imgSrc: require("../menu/img/slot_file_icon.png"),
    activeOnState: "main.machines.**",
    excludeActiveOnState: "main.machines.progressives.**",
  });

  if (isModuleLoaded("mealLog")) {
    machineMenuItem.addChild(
      new MenuItem({
        id: "machines.mealLog",
        state: "main.mealLog.grid",
        display: "MEAL Log",
        requiredPermission: "mealLogView",
      })
    );
  }

  if (isModuleLoaded("contracts")) {
    machineMenuItem.addChild(
      new MenuItem({
        id: "machines.contracts",
        state: "main.contracts.grid",
        display: "Contracts",
        requiredPermission: "contractRegulatoryView",
      })
    );
  }

  machineMenuItem.addChild(
    new MenuItem({
      id: "machines.progressives",
      state: "main.machines.progressives",
      display: "Progressives",
    })
  );

  machineMenuItem.addChild(
    new MenuItem({
      id: "machines.software",
      state: "main.machines.software",
      display: "Machine software",
    })
  );

  machineMenuItem.addChild(
    new MenuItem({
      id: "machines.lookups",
      state: "main.machines.lookups",
      display: "Lookup Management",
    })
  );

  return machineMenuItem;
}

function projectMenu(): MenuItem {
  const projectMenuItem: MenuItem = new MenuItem({
    id: "projects",
    state: "main.projects.grid",
    display: "Projects",
    imgSrc: "/res/slot_file_icon.png",
    activeOnState: "main.projects.**",
  });

  return projectMenuItem;
}
